<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('habilitation_import.title') }}
      </oxd-text>
      <oxd-divider />

      <div class="habilitation-import-info">
        <oxd-text tag="p" class="habilitation-import-info__title">
          {{ $t('habilitation_import.info_title') }}
        </oxd-text>
        <ul>
          <li>{{ $t('habilitation_import.info_columns') }}</li>
          <li>{{ $t('habilitation_import.info_matching') }}</li>
          <li>{{ $t('habilitation_import.info_dates') }}</li>
          <li>{{ $t('habilitation_import.info_duplicates') }}</li>
          <li>{{ $t('habilitation_import.info_no_delete') }}</li>
        </ul>
      </div>

      <oxd-form :loading="isLoading" @submit-valid="onPreview">
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="attachment.attachment"
                type="file"
                :rules="rules.attachment"
                :label="$t('general.select_file')"
                :button-label="$t('general.browse')"
                :placeholder="$t('general.no_file_selected')"
                required
              />
              <oxd-text class="orangehrm-input-hint" tag="p">
                {{
                  $t('general.accepts_up_to_n_mb', {count: formattedFileSize})
                }}
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            type="submit"
            display-type="secondary"
            :label="$t('habilitation_import.analyze')"
          />
        </oxd-form-actions>
      </oxd-form>

      <div v-if="errorMessage" class="habilitation-import-alert" role="alert">
        {{ errorMessage }}
      </div>

      <template v-if="summary">
        <oxd-divider />
        <div class="habilitation-import-summary">
          <div
            v-for="card in summaryCards"
            :key="card.key"
            class="habilitation-import-summary__card"
            :class="`habilitation-import-summary__card--${card.key}`"
          >
            <span class="habilitation-import-summary__value">{{
              card.value
            }}</span>
            <span class="habilitation-import-summary__label">{{
              card.label
            }}</span>
          </div>
        </div>

        <div
          v-if="summary.newLicenseTypes?.length && !committed"
          class="habilitation-import-new-types"
        >
          <strong>{{ $t('habilitation_import.new_types') }} :</strong>
          {{ summary.newLicenseTypes.join(', ') }}
        </div>

        <div v-if="result" class="habilitation-import-result" role="status">
          <strong>{{ $t('habilitation_import.import_done') }}</strong>
          <span>
            {{
              $t('habilitation_import.import_result', {
                added: result.added ?? 0,
                updated: result.updated ?? 0,
                notImported: result.notImported ?? 0,
              })
            }}
          </span>
        </div>

        <div class="habilitation-import-toolbar">
          <div class="habilitation-import-filter">
            <oxd-input-field
              v-model="filterOption"
              type="select"
              :label="$t('habilitation_import.filter')"
              :options="filterOptions"
              :clear="false"
            />
          </div>
          <oxd-button
            display-type="ghost"
            icon-name="download"
            :label="$t('habilitation_import.download_report')"
            @click="onDownloadReport"
          />
        </div>

        <oxd-text tag="p" class="habilitation-import-count">
          {{
            $t('habilitation_import.displayed_rows', {
              count: filteredRows.length,
              total: rows.length,
            })
          }}
        </oxd-text>

        <div class="habilitation-import-table-wrapper">
          <table class="habilitation-import-table">
            <thead>
              <tr>
                <th>{{ $t('habilitation_import.column_line') }}</th>
                <th>{{ $t('habilitation_import.column_employee') }}</th>
                <th>{{ $t('habilitation_import.column_employee_id') }}</th>
                <th>{{ $t('habilitation_import.column_habilitation') }}</th>
                <th>{{ $t('habilitation_import.column_start') }}</th>
                <th>{{ $t('habilitation_import.column_end') }}</th>
                <th>{{ $t('habilitation_import.column_status') }}</th>
                <th>{{ $t('habilitation_import.column_detail') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in pagedRows" :key="row.rowNumber">
                <td class="habilitation-import-table__number">
                  {{ row.rowNumber }}
                </td>
                <td>{{ row.employeeName || row.person || '—' }}</td>
                <td>{{ row.employeeId || '—' }}</td>
                <td>
                  {{ row.habilitation || '—' }}
                  <span
                    v-if="row.newLicenseType"
                    class="habilitation-import-badge habilitation-import-badge--new"
                  >
                    {{ $t('habilitation_import.new_type') }}
                  </span>
                </td>
                <td>{{ formatDisplayDate(row.startDate) }}</td>
                <td>{{ formatDisplayDate(row.endDate) }}</td>
                <td>
                  <span
                    class="habilitation-import-badge"
                    :class="`habilitation-import-badge--${row.action}`"
                  >
                    {{ $t(`habilitation_import.status_${row.action}`) }}
                  </span>
                </td>
                <td>
                  <div>{{ row.message }}</div>
                  <div
                    v-if="row.currentStartDate || row.currentEndDate"
                    class="habilitation-import-table__current"
                  >
                    {{
                      $t('habilitation_import.current_dates', {
                        start: formatDisplayDate(row.currentStartDate),
                        end: formatDisplayDate(row.currentEndDate),
                      })
                    }}
                  </div>
                </td>
              </tr>
              <tr v-if="pagedRows.length === 0">
                <td colspan="8" class="habilitation-import-table__empty">
                  {{ $t('habilitation_import.no_rows') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="pageCount > 1" class="orangehrm-bottom-container">
          <oxd-pagination v-model:current="currentPage" :length="pageCount" />
        </div>

        <oxd-divider />
        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            :label="$t('habilitation_import.new_file')"
            @click="onReset"
          />
          <oxd-button
            display-type="secondary"
            :label="
              committed
                ? $t('habilitation_import.imported')
                : $t('habilitation_import.confirm_import')
            "
            :disabled="committed || summary.importable === 0 || isLoading"
            @click="onCommit"
          />
        </oxd-form-actions>
      </template>
    </div>
  </div>
</template>

<script>
import {
  required,
  maxFileSize,
  validFileTypes,
} from '@/core/util/validation/rules';
import {APIService} from '@/core/util/services/api.service';

const PAGE_SIZE = 25;

export default {
  name: 'HabilitationImport',

  props: {
    allowedFileTypes: {
      type: Array,
      required: true,
    },
    maxFileSize: {
      type: Number,
      required: true,
    },
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/habilitation-import',
    );
    return {http};
  },

  data() {
    return {
      isLoading: false,
      attachment: {attachment: null},
      rows: [],
      summary: null,
      result: null,
      committed: false,
      errorMessage: '',
      filter: 'all',
      currentPage: 1,
      rules: {
        attachment: [
          required,
          maxFileSize(this.maxFileSize),
          validFileTypes(this.allowedFileTypes),
        ],
      },
    };
  },

  computed: {
    formattedFileSize() {
      return Math.round((this.maxFileSize / (1024 * 1024)) * 100) / 100;
    },
    filterOptions() {
      return [
        {id: 'all', label: this.$t('habilitation_import.filter_all')},
        {
          id: 'importable',
          label: this.$t('habilitation_import.filter_importable'),
        },
        {id: 'review', label: this.$t('habilitation_import.filter_review')},
        {id: 'ignored', label: this.$t('habilitation_import.filter_ignored')},
        {
          id: 'unchanged',
          label: this.$t('habilitation_import.filter_unchanged'),
        },
      ];
    },
    summaryCards() {
      if (!this.summary) {
        return [];
      }
      return [
        {
          key: 'total',
          value: this.summary.total ?? 0,
          label: this.$t('habilitation_import.summary_total'),
        },
        {
          key: 'importable',
          value: this.summary.importable ?? 0,
          label: this.$t('habilitation_import.summary_importable'),
        },
        {
          key: 'add',
          value: this.summary.add ?? 0,
          label: this.$t('habilitation_import.summary_add'),
        },
        {
          key: 'update',
          value: this.summary.update ?? 0,
          label: this.$t('habilitation_import.summary_update'),
        },
        {
          key: 'unchanged',
          value: this.summary.unchanged ?? 0,
          label: this.$t('habilitation_import.summary_unchanged'),
        },
        {
          key: 'ignored',
          value: (this.summary.ignored ?? 0) + (this.summary.superseded ?? 0),
          label: this.$t('habilitation_import.summary_ignored'),
        },
        {
          key: 'review',
          value: (this.summary.review ?? 0) + (this.summary.error ?? 0),
          label: this.$t('habilitation_import.summary_review'),
        },
      ];
    },
    filterOption: {
      get() {
        return (
          this.filterOptions.find((option) => option.id === this.filter) ||
          this.filterOptions[0]
        );
      },
      set(option) {
        this.filter = option?.id ?? 'all';
        this.currentPage = 1;
      },
    },
    filteredRows() {
      switch (this.filter) {
        case 'importable':
          return this.rows.filter((row) =>
            ['add', 'update'].includes(row.action),
          );
        case 'review':
          return this.rows.filter((row) =>
            ['review', 'error'].includes(row.action),
          );
        case 'ignored':
          return this.rows.filter((row) =>
            ['ignored', 'superseded'].includes(row.action),
          );
        case 'unchanged':
          return this.rows.filter((row) => row.action === 'unchanged');
        default:
          return this.rows;
      }
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredRows.length / PAGE_SIZE));
    },
    pagedRows() {
      const start = (this.currentPage - 1) * PAGE_SIZE;
      return this.filteredRows.slice(start, start + PAGE_SIZE);
    },
  },

  methods: {
    onPreview() {
      if (!this.attachment.attachment) {
        return;
      }
      this.isLoading = true;
      this.errorMessage = '';
      this.result = null;
      this.committed = false;
      this.http
        .create({...this.attachment, mode: 'preview'})
        .then((response) => {
          this.rows = response.data.data ?? [];
          this.summary = response.data.meta ?? {};
          this.filter = 'all';
          this.currentPage = 1;
        })
        .catch((error) => {
          this.errorMessage = this.getErrorMessage(error);
          this.rows = [];
          this.summary = null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onCommit() {
      if (!this.attachment.attachment || !this.summary?.importable) {
        return;
      }
      this.isLoading = true;
      this.errorMessage = '';
      this.http
        .create({...this.attachment, mode: 'commit'})
        .then((response) => {
          this.result = response.data.meta ?? {};
          this.committed = true;
          this.$toast.saveSuccess();
        })
        .catch((error) => {
          this.errorMessage = this.getErrorMessage(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onReset() {
      this.attachment = {attachment: null};
      this.rows = [];
      this.summary = null;
      this.result = null;
      this.committed = false;
      this.errorMessage = '';
      this.filter = 'all';
      this.currentPage = 1;
    },
    onDownloadReport() {
      const headers = [
        this.$t('habilitation_import.column_line'),
        this.$t('habilitation_import.column_employee'),
        this.$t('habilitation_import.column_employee_id'),
        this.$t('habilitation_import.column_habilitation'),
        this.$t('habilitation_import.column_start'),
        this.$t('habilitation_import.column_end'),
        this.$t('habilitation_import.column_status'),
        this.$t('habilitation_import.column_detail'),
      ];
      const lines = [
        headers,
        ...this.rows.map((row) => [
          row.rowNumber,
          row.employeeName || row.person || '',
          row.employeeId || '',
          row.habilitation || '',
          row.startDate || '',
          row.endDate || '',
          this.$t(`habilitation_import.status_${row.action}`),
          row.message || '',
        ]),
      ];
      const csv = lines
        .map((line) =>
          line
            .map((value) => `"${String(value).replace(/"/g, '""')}"`)
            .join(';'),
        )
        .join('\r\n');
      const blob = new Blob([`\uFEFF${csv}`], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'import-habilitations.csv';
      link.click();
      URL.revokeObjectURL(url);
    },
    formatDisplayDate(value) {
      if (!value) {
        return '—';
      }
      const [year, month, day] = String(value).split('-');
      return year && month && day ? `${day}/${month}/${year}` : value;
    },
    getErrorMessage(error) {
      return (
        error?.response?.data?.error?.message ||
        this.$t('habilitation_import.error_generic')
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.habilitation-import-info {
  background-color: $oxd-interface-gray-lighten-2-color;
  border-radius: 1.2rem;
  padding: 1.2rem;
  margin-bottom: 1.2rem;
  color: $oxd-input-control-font-color;
  font-size: $oxd-input-control-font-size;

  ul {
    margin: 0.6rem 0 0;
    padding-left: 1.4rem;
  }

  li {
    margin-bottom: 0.3rem;
  }
}

.habilitation-import-info__title {
  font-weight: 700;
}

.habilitation-import-alert {
  margin-top: 1rem;
  padding: 0.8rem 1rem;
  border-radius: 0.6rem;
  color: $oxd-feedback-danger-color;
  background-color: rgba($oxd-feedback-danger-color, 0.08);
}

.habilitation-import-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 0.8rem;
  margin-bottom: 1rem;
}

.habilitation-import-summary__card {
  display: flex;
  flex-direction: column;
  padding: 0.8rem;
  border-radius: 0.8rem;
  background-color: $oxd-interface-gray-lighten-2-color;
  border-top: 3px solid $oxd-interface-gray-color;
}

.habilitation-import-summary__card--importable,
.habilitation-import-summary__card--add {
  border-top-color: $oxd-feedback-success-color;
}

.habilitation-import-summary__card--update {
  border-top-color: $oxd-primary-one-color;
}

.habilitation-import-summary__card--review {
  border-top-color: $oxd-feedback-warn-color;
}

.habilitation-import-summary__value {
  font-size: 1.4rem;
  font-weight: 700;
  color: $oxd-interface-gray-darken-2-color;
}

.habilitation-import-summary__label {
  font-size: 0.75rem;
  color: $oxd-interface-gray-color;
}

.habilitation-import-new-types {
  margin-bottom: 1rem;
  padding: 0.8rem 1rem;
  border-radius: 0.6rem;
  background-color: rgba($oxd-primary-one-color, 0.08);
  color: $oxd-interface-gray-darken-1-color;
}

.habilitation-import-result {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.8rem 1rem;
  border-radius: 0.6rem;
  color: $oxd-feedback-success-color;
  background-color: rgba($oxd-feedback-success-color, 0.08);
}

.habilitation-import-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
}

.habilitation-import-filter {
  width: 240px;
}

.habilitation-import-count {
  margin: 0.5rem 0;
  font-size: 0.8rem;
  color: $oxd-interface-gray-color;
}

.habilitation-import-table-wrapper {
  width: 100%;
  overflow-x: auto;
  border: 1px solid $oxd-interface-gray-lighten-1-color;
  border-radius: 0.8rem;
}

.habilitation-import-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;

  th,
  td {
    padding: 0.65rem 0.75rem;
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid $oxd-interface-gray-lighten-2-color;
  }

  th {
    background-color: $oxd-interface-gray-lighten-2-color;
    color: $oxd-interface-gray-darken-1-color;
    font-weight: 700;
    white-space: nowrap;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
}

.habilitation-import-table__number {
  color: $oxd-interface-gray-color;
  white-space: nowrap;
}

.habilitation-import-table__current {
  margin-top: 0.25rem;
  font-size: 0.72rem;
  color: $oxd-interface-gray-color;
}

.habilitation-import-table__empty {
  padding: 2rem !important;
  text-align: center !important;
  color: $oxd-interface-gray-color;
}

.habilitation-import-badge {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 0.4rem;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
  color: $oxd-interface-gray-darken-1-color;
  background-color: $oxd-interface-gray-lighten-2-color;
}

.habilitation-import-badge--add {
  color: $oxd-feedback-success-color;
  background-color: rgba($oxd-feedback-success-color, 0.1);
}

.habilitation-import-badge--update,
.habilitation-import-badge--new {
  color: $oxd-primary-one-color;
  background-color: rgba($oxd-primary-one-color, 0.1);
}

.habilitation-import-badge--review,
.habilitation-import-badge--ignored {
  color: $oxd-feedback-warn-color;
  background-color: rgba($oxd-feedback-warn-color, 0.12);
}

.habilitation-import-badge--error {
  color: $oxd-feedback-danger-color;
  background-color: rgba($oxd-feedback-danger-color, 0.1);
}

@media (max-width: 768px) {
  .habilitation-import-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .habilitation-import-filter {
    width: 100%;
  }
}
</style>
